// US-108 — Platform Task Subscriptions Engine: the CODE-owned task registry and
// its shared wire types. LivingSyncs owns the task definitions here (never
// authored at runtime); facilities *subscribe*; the system derives or generates
// role-based work. Pure types + a const registry — no server deps (imported by
// both backend and frontend).
//
// Two layers, kept separate (see the spec):
//   - Engine (write side): this registry + `createTask` + the generation sweep.
//   - Read model (read side): merges derived (compute-on-read) + stateful rows.
//
// Adding a task = ONE entry in `TASK_DEFINITIONS` — no new scheduler job, no
// table, no per-facility authoring.

import type { PermissionKey, AppRole } from "./auth";
import type { DomainEvent } from "./events";
import type { FormFrequency } from "./enums";
import type { ResidentTaskKind } from "./tasks";

// ── Vocabulary ────────────────────────────────────────────────────────────────

export const TASK_DOMAIN_VALUES = [
  "resident_care",
  "training",
  "medication",
  "compliance",
  "oversight",
] as const;
export type TaskDomain = (typeof TASK_DOMAIN_VALUES)[number];

export const TASK_SUBJECT_TYPE_VALUES = [
  "resident",
  "employee",
  "facility",
] as const;
export type TaskSubjectType = (typeof TASK_SUBJECT_TYPE_VALUES)[number];

/**
 * Which read model an instance surfaces in:
 *   - `role_pool`    → anyone holding the definition's `requiredPermission` at
 *                      the facility (resident care → Today's Tasks).
 *   - `per_assignee` → only the assigned employee (staff training → My Tasks).
 * Manager oversight (facility dashboard) is a separate `staff:manage`-gated read,
 * not a per-definition visibility.
 */
export const TASK_VISIBILITY_SCOPE_VALUES = [
  "role_pool",
  "per_assignee",
] as const;
export type TaskVisibilityScope = (typeof TASK_VISIBILITY_SCOPE_VALUES)[number];

export const TASK_INSTANCE_STATUS_VALUES = [
  "open",
  "complete",
  "overdue",
  "missed",
  "cancelled",
] as const;
export type TaskInstanceStatus = (typeof TASK_INSTANCE_STATUS_VALUES)[number];

/** Terminal statuses — no longer actionable; the retention-purge target. */
export const TASK_TERMINAL_STATUS_VALUES = [
  "complete",
  "missed",
  "cancelled",
] as const satisfies readonly TaskInstanceStatus[];
export type TaskTerminalStatus = (typeof TASK_TERMINAL_STATUS_VALUES)[number];

/** Statuses that still surface in a worklist read (actionable / attention-needed). */
export const TASK_ACTIVE_STATUS_VALUES = [
  "open",
  "overdue",
] as const satisfies readonly TaskInstanceStatus[];

export const TASK_SUBSCRIPTION_SCOPE_VALUES = ["org", "facility"] as const;
export type TaskSubscriptionScope =
  (typeof TASK_SUBSCRIPTION_SCOPE_VALUES)[number];

/** Domain record a `derived` task defers "done" to — never re-owned by the engine. */
export const TASK_COMPLETION_SOURCE_VALUES = [
  "medication_administrations",
  "vital_signs",
  "adl_records",
  "form_submission",
  "training_completions",
  "medication_orders",
  // US-108 matrix worklist sources.
  "care_plans",
  "care_notes",
  "incidents",
  "cases",
  "audit_findings",
  "external_provider_form_assignments",
  "resident_form_requirements",
  "subscription",
  "training_assignments",
  "appointments",
  // US-010 follow-up: the resident's OWN admission-data fields (room, move-in
  // date, allergies, diagnoses, mobility level) — not a related table's
  // activity, but a live completeness check on the subject row itself.
  "residents",
  // US-010 follow-up: resident<->physician links (resident_external_providers +
  // external_providers) — reuses analyzePhysicians' exact "no physician / no
  // primary / incomplete record" logic from the compliance dashboard.
  "resident_external_providers",
  // US-010 follow-up: combined pharmacy + insurance completeness — mirrors the
  // compliance dashboard's single "Pharmacy & Insurance" category
  // (analyzePharmacyInsurance reads both resident_pharmacies AND
  // resident_insurance together).
  "resident_pharmacy_insurance",
] as const;
export type TaskCompletionSource =
  (typeof TASK_COMPLETION_SOURCE_VALUES)[number];

// ── Definition shape ───────────────────────────────────────────────────────────

export type TaskTrigger =
  | { type: "recurring"; frequency: FormFrequency }
  | { type: "event"; on: DomainEvent["type"] }
  | { type: "manual"; managerInstantiable?: boolean };

export type TaskCompletion =
  { mode: "manual" } | { mode: "derived"; source: TaskCompletionSource };

// ── Per-facility configuration (US-111) ─────────────────────────────────────────
//
// A facility/org that has subscribed to a CODE-defined task may tune it. The
// tuning lives as JSON on the `task_subscriptions` row (role_policy / due_policy)
// + the `priority_override` column — never on the code definition, which stays the
// platform-owned catalog. The definition's `requiredPermission` is ALWAYS the hard
// authorization floor; config can only NARROW, never widen (see TaskRolePolicy).

/**
 * Overrides a definition's display owner and (optionally) narrows its audience.
 * The `requiredPermission` capability gate is applied FIRST and is never relaxed
 * by this policy — `visibleToRoles` is a pure post-gate filter.
 */
export interface TaskRolePolicy {
  /**
   * Overrides `def.assignedRole`: the display "who's it for" + the role stamped
   * onto created stateful instances. Must be a role that HOLDS
   * `def.requiredPermission` (else the owner couldn't complete it — guardrail #10).
   */
  ownerRole?: AppRole;
  /**
   * Opt-in audience narrowing. When set & non-empty, a viewer sees the task only
   * if they ALSO hold one of these roles. Absent/empty = every capable viewer
   * (today's behavior). Filter only — can never grant visibility past the
   * capability gate.
   */
  visibleToRoles?: AppRole[];
}

/** Cadence override for frequency-driven tasks (recurring stateful generation). */
export interface TaskDuePolicy {
  /** Overrides the definition's recurring frequency; validated against
   *  FORM_FREQUENCY_VALUES. Ignored by SLA-interval derived defs (Phase 1). */
  frequency?: FormFrequency;
}

export interface TaskDefinition {
  /** Stable key — the source of truth; persisted on instances/subscriptions. */
  key: string;
  domain: TaskDomain;
  subjectType: TaskSubjectType;
  /**
   * Semantic owner — display / "who's it for". Stamped onto `assigned_role`.
   * NOT the gate: role↔capability is non-obvious (care staff administer meds but
   * are read-only on vitals), so authority always flows from `requiredPermission`.
   */
  assignedRole: AppRole;
  /**
   * THE GATE — the capability to perform/complete the work. Drives role_pool
   * visibility and manual check-off authority. Gate on this, never the role name.
   */
  requiredPermission: PermissionKey;
  trigger: TaskTrigger;
  completion: TaskCompletion;
  visibility: TaskVisibilityScope;
  /** Premium-only definition — its config surface is `requirePremiumTier`-gated. */
  premium: boolean;
  /**
   * Base safety task (meds/vitals/ADL/forms): active for EVERY facility with no
   * subscription required and NEVER premium-gated — preserves today's Phase-0
   * behavior. Non-base definitions require an active subscription to be on.
   */
  baseSafety: boolean;
  title: string;
  /** For derived resident-care tasks, the Today's-Tasks kind this maps to. */
  derivedKind?: ResidentTaskKind;
}

// ── The registry (CODE source of truth) ─────────────────────────────────────────
//
// Phase 1 seeds the four base-safety derived resident tasks so the resident
// "Today's Tasks" read behaves identically once it is driven by the registry.
// The Phase-0 permission map is preserved exactly:
//   meds → medication:administer · vitals → vital-sign:write
//   adl  → adl-record:write      · form   → form-submission:write

export const TASK_DEFINITIONS: readonly TaskDefinition[] = [
  {
    key: "resident_care.medications",
    domain: "medication",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "medication:administer",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "medication_administrations" },
    visibility: "role_pool",
    premium: false,
    baseSafety: true,
    title: "Medications",
    derivedKind: "medications",
  },
  {
    // Care + medical both record vitals (care granted vital-sign:write — see
    // gen-role-grants GRANT_ADDITIONS); owned by the lowest such role (care).
    key: "resident_care.vitals",
    domain: "resident_care",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "vital-sign:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "vital_signs" },
    visibility: "role_pool",
    premium: false,
    baseSafety: true,
    title: "Record Vitals",
    derivedKind: "vitals",
  },
  {
    key: "resident_care.adl",
    domain: "resident_care",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "adl-record:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "adl_records" },
    visibility: "role_pool",
    premium: false,
    baseSafety: true,
    title: "Chart ADL",
    derivedKind: "adl",
  },
  {
    key: "resident_care.form",
    domain: "compliance",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "form-submission:write",
    // Windowing is per-requirement (each unfiled requirement is its own task);
    // the derived read computes them rowless from resident_form_requirements.
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "form_submission" },
    visibility: "role_pool",
    premium: false,
    baseSafety: true,
    title: "Forms",
    derivedKind: "form",
  },
  {
    // Today's appointments + their unfilled required forms (appointment_required_
    // forms). Base-safety resident-board kind: shows the schedule and the
    // appointment-linked forms care staff complete during the visit. Gated on
    // appointment:read (care + medical + manager + org all hold it); per-record
    // completion (appointment status / form submission).
    key: "resident_care.appointment",
    domain: "resident_care",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "appointment:read",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "appointments" },
    visibility: "role_pool",
    premium: false,
    baseSafety: true,
    title: "Appointments",
    derivedKind: "appointment",
  },
  {
    // Care staff provide a daily update to each OPEN care plan. Derived from the
    // plan's progress_notes — "done" for the day = a progress note was added
    // today (the existing POST .../progress-notes path, gated on care-plan:
    // document, which care staff hold). DISTINCT from resident_care.care_plan_
    // review (medical's periodic review, a worklist task): this is the daily
    // care-staff progress note, on the resident board next to vitals/ADL.
    key: "resident_care.care_plan_update",
    domain: "resident_care",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "care-plan:document",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "care_plans" },
    visibility: "role_pool",
    premium: false,
    // Opt-in (US-108 review): not every facility logs a daily progress note per
    // open care plan — subscription-gated, off by default, so the resident board
    // isn't flooded with permanently-red cards (alarm fatigue).
    baseSafety: false,
    title: "Update care plans",
    derivedKind: "care_plan_update",
  },
  {
    // Daily resident check-in. "Done" for the day = a 'general' care note was
    // logged today for the resident (the "Check In" quick action → care-notes
    // flow). Gated on care-note:write (care staff hold it). Resident board.
    key: "resident_care.check_in",
    domain: "resident_care",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "care-note:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "care_notes" },
    visibility: "role_pool",
    premium: false,
    // Opt-in (US-108 review): a daily 'general' check-in note isn't universal —
    // subscription-gated, off by default.
    baseSafety: false,
    title: "Check in",
    derivedKind: "check_in",
  },
  {
    // Daily shift note for the resident. "Done" = a 'shift' care note
    // (care_notes.note_type='shift') was logged today. Distinct from the
    // facility-level shift_notes handoff log. Resident board.
    key: "resident_care.shift_note",
    domain: "resident_care",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "care-note:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "care_notes" },
    visibility: "role_pool",
    premium: false,
    // Opt-in (US-108 review): per-resident daily shift notes are a facility-
    // specific workflow — subscription-gated, off by default.
    baseSafety: false,
    title: "Shift note",
    derivedKind: "shift_note",
  },
  {
    // WEEKLY: care staff manage each resident's appointment scheduling. "Done"
    // for the week = an appointment was created for the resident this week
    // (the booking signal). Gated on appointment:write — RE-GRANTED to care for
    // this task (see gen-role-grants WRITE_REMOVALS history). Distinct from the
    // daily resident_care.appointment kind (today's visits + their forms).
    key: "resident_care.appointment_scheduling",
    domain: "resident_care",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "appointment:write",
    trigger: { type: "recurring", frequency: "weekly" },
    completion: { mode: "derived", source: "appointments" },
    visibility: "role_pool",
    premium: false,
    // Opt-in (US-108 review): weekly per-resident appointment scheduling isn't a
    // universal cadence — subscription-gated, off by default.
    baseSafety: false,
    title: "Schedule appointments",
    derivedKind: "appointment_scheduling",
  },
  // ── Derived WORKLIST definitions (no derivedKind → My Tasks / dashboard, not
  //    the resident board). Computed live; require a subscription. ──
  {
    // Medical staff re-review each active order every 30 days. Derived from
    // medication_orders.last_reviewed_at; surfaces as a worklist item per
    // due order. Gate on the capability to manage orders (medication:write).
    key: "medication.order_review",
    domain: "medication",
    subjectType: "resident",
    assignedRole: "role:staff:medical",
    requiredPermission: "medication:write",
    trigger: { type: "recurring", frequency: "monthly" },
    completion: { mode: "derived", source: "medication_orders" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Review medication orders",
  },
  {
    // BT-20: surface pharmacy-pushed orders awaiting initial review (review_status
    // = pending) on medical staff's My Tasks immediately, not just via the 24h
    // escalation notification. Distinct from order_review (approved re-review).
    key: "medication.pending_order_review",
    domain: "medication",
    subjectType: "resident",
    assignedRole: "role:staff:medical",
    requiredPermission: "medication:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "medication_orders" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Review pending pharmacy order",
  },
  {
    // Care-plan review due — derived from each plan's review_frequency →
    // next_review_date (US-100). Care plans are CREATED/CLOSED (reviewed) by
    // medical staff + facility managers (care-plan:write); care staff can update
    // a plan (care-plan:document) but don't own the periodic review.
    key: "resident_care.care_plan_review",
    domain: "resident_care",
    subjectType: "resident",
    // Lowest role that can review/close (care-plan:write): medical staff.
    assignedRole: "role:staff:medical",
    requiredPermission: "care-plan:write",
    trigger: { type: "recurring", frequency: "monthly" },
    completion: { mode: "derived", source: "care_plans" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Care plan reviews",
  },
  {
    // Resolve open incidents (CAPA) — overdue 7 days after the incident.
    key: "incident.resolve",
    domain: "oversight",
    subjectType: "facility",
    // Lowest role that can resolve (incident:write): medical staff (not care).
    assignedRole: "role:staff:medical",
    requiredPermission: "incident:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "incidents" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Resolve open incidents",
  },
  {
    // Assign unassigned cases — overdue 3 business days after creation.
    key: "case.assign",
    domain: "oversight",
    subjectType: "facility",
    assignedRole: "role:facility:manager",
    // Case management (assign/close) is manager + org only: gate on case:close
    // (the manager/org case action) — case:write is held by all clinical roles.
    requiredPermission: "case:close",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "cases" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Assign unassigned cases",
  },
  {
    // Close cases past their due date. Manager + org only (case:close).
    key: "case.close_due",
    domain: "oversight",
    subjectType: "facility",
    assignedRole: "role:facility:manager",
    requiredPermission: "case:close",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "cases" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Close cases past due",
  },
  {
    // Resolve audit findings needing follow-up — due by follow_up_date. Owned by
    // the facility side that responds (manager + org, who hold audit:write);
    // auditors also hold audit:write and may track their findings' follow-up.
    key: "compliance.audit_finding_followup",
    domain: "compliance",
    subjectType: "facility",
    assignedRole: "role:facility:manager",
    requiredPermission: "audit:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "audit_findings" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Audit finding follow-up",
  },
  {
    // Chase active external-provider form assignments; due by effective_until.
    // Care + medical handle these (both hold external-provider:write); owned by
    // the lowest such role (care).
    key: "external_provider.assignment_followup",
    domain: "compliance",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "external-provider:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: {
      mode: "derived",
      source: "external_provider_form_assignments",
    },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "External-provider form follow-up",
  },
  {
    // Manager/admin oversight: new residents with outstanding ADMISSION forms
    // (one_shot + due_anchor='admission'). Packet-level — one card per resident.
    // Onboarding is a coordinator responsibility, so gate on automation:manage
    // (facility manager + org admin only) — NOT resident:write, which clinical
    // staff also hold. The per-form filing is NOT on the resident daily board
    // (admission/rule-bundle forms are excluded from resident_care.form).
    key: "compliance.admission_packet",
    domain: "compliance",
    subjectType: "resident",
    assignedRole: "role:facility:manager",
    requiredPermission: "automation:manage",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "resident_form_requirements" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "New-admission forms",
  },
  {
    // Manager/admin oversight: residents with outstanding RECURRING state-
    // compliance forms fanned from an adopted rule bundle (source='rule_bundle',
    // excluding the admission one-shots above). Packet-level — one card per
    // resident. Coordinator work like admission (automation:manage = facility
    // manager + org admin), kept OFF the frontline daily board so clinical staff
    // aren't flooded with the compliance battery (e.g. the IL CILA assessments).
    key: "compliance.state_compliance_forms",
    domain: "compliance",
    subjectType: "resident",
    assignedRole: "role:facility:manager",
    requiredPermission: "automation:manage",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "resident_form_requirements" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Complete state-compliance forms",
  },
  {
    // US-010 follow-up: replaces the old dashboard "Action Items" card (a
    // client-side check with no detail and no action path). Packet-level —
    // one card per resident missing any of 5 admission fields (room, move-in
    // date, allergies, diagnoses, mobility level), naming exactly what's
    // missing. Derived straight off the resident row itself (source:
    // "residents") — self-clears the moment the fields are filled in, no
    // separate "mark complete" step. Base-safety: the old card was
    // unconditional for every facility with no opt-out: preserve that.
    key: "compliance.resident_profile_incomplete",
    domain: "compliance",
    subjectType: "resident",
    assignedRole: "role:facility:manager",
    requiredPermission: "resident:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "residents" },
    visibility: "role_pool",
    premium: false,
    baseSafety: true,
    title: "Complete resident profile",
  },
  {
    // US-010 follow-up: same "Top Issues Across Facility" gap the audit
    // compliance dashboard already surfaces (analyzePhysicians), now also a
    // worklist task. One card per resident with no physician, no primary
    // physician, or an incomplete physician record. Gated on
    // resident-directory:write — the same permission that shows the "Add
    // Provider" action on the Contacts & Admin tab.
    key: "compliance.resident_physicians_incomplete",
    domain: "compliance",
    subjectType: "resident",
    assignedRole: "role:facility:manager",
    requiredPermission: "resident-directory:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "resident_external_providers" },
    visibility: "role_pool",
    premium: false,
    baseSafety: true,
    title: "Update physician information",
  },
  {
    // US-010 follow-up: same "Pharmacy & Insurance" gap the audit compliance
    // dashboard already surfaces (analyzePharmacyInsurance), now also a
    // worklist task. One card per resident missing pharmacy and/or insurance
    // info. Gated on resident-directory:write, same as the physicians task.
    key: "compliance.resident_pharmacy_insurance_incomplete",
    domain: "compliance",
    subjectType: "resident",
    assignedRole: "role:facility:manager",
    requiredPermission: "resident-directory:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "resident_pharmacy_insurance" },
    visibility: "role_pool",
    premium: false,
    baseSafety: true,
    title: "Update pharmacy & insurance information",
  },
  {
    // US-116: facility-scope recurring forms. One worklist card per
    // `audience='facility'` form assigned to the facility (form_template_facilities)
    // whose own `form_templates.frequency` window has no submission yet. Completion
    // is a form_submissions row with resident_id=null in the window — the provider
    // (facilityFormProvider) windows on EACH template's frequency, so the def-level
    // frequency below is nominal (recurring+derived+no-derivedKind ⇒ NOT cadence-
    // configurable; the US-111 panel hides its cadence picker). Opt-in (baseSafety
    // false): a facility enables "Facility forms" to start receiving the reminders.
    key: "compliance.facility_form",
    domain: "compliance",
    subjectType: "facility",
    assignedRole: "role:facility:manager",
    requiredPermission: "form-submission:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "form_submission" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Facility forms",
  },
  {
    // Staff training assignments overdue/uncompleted (manager oversight).
    key: "training.assignment_due",
    domain: "training",
    subjectType: "employee",
    assignedRole: "role:facility:manager",
    requiredPermission: "training-assignment:write",
    trigger: { type: "recurring", frequency: "daily" },
    // Provenance source = where "done" is actually read: the provider derives
    // not-done from training_assignments.completion_date IS NULL (label matches
    // the reader, per review).
    completion: { mode: "derived", source: "training_assignments" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Staff training due",
  },
  {
    // The facility's org subscription nearing its renewal date (org-admin).
    key: "billing.subscription_renewal",
    domain: "oversight",
    subjectType: "facility",
    assignedRole: "role:org:admin",
    requiredPermission: "subscription:write",
    trigger: { type: "recurring", frequency: "daily" },
    completion: { mode: "derived", source: "subscription" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Subscription renewal",
  },
  // ── Phase 2: stateful definitions (materialize rows; require a subscription) ──
  {
    // New-admission intake: opened on resident.created when the facility is
    // subscribed. Manual check-off; gated on form-submission:write so the full
    // care team (care + medical + manager + org) who complete the intake forms
    // can see and close it. Owned by the lowest such role (care).
    key: "resident_care.intake",
    domain: "resident_care",
    subjectType: "resident",
    assignedRole: "role:staff:care",
    requiredPermission: "form-submission:write",
    trigger: { type: "event", on: "resident.created" },
    completion: { mode: "manual" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Complete new admission intake",
  },
  {
    // Staff certification renewal: opened on certification.expiring. The employee
    // is the SUBJECT, but renewal is a MANAGER responsibility — so it is role_pool
    // gated on training-assignment:write (held by facility managers / org admins),
    // NOT pinned to the cert-holder who lacks that capability. Aligning visibility
    // with the completion gate avoids the Phase-0 trap (guardrail #10): surfacing
    // a task to someone who can't complete it.
    key: "training.certification_renewal",
    domain: "training",
    subjectType: "employee",
    assignedRole: "role:facility:manager",
    requiredPermission: "training-assignment:write",
    trigger: { type: "event", on: "certification.expiring" },
    completion: { mode: "manual" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Renew expiring certification",
  },
  {
    // SCRUM-153: an appointment needs external-provider forms but the facility
    // has no active provider to assign them to. Prompt a manager to add one.
    // Gated on external-provider:manage (who can add a provider) and assigned
    // to the facility manager — visibility aligned with the completion gate.
    key: "external_provider.no_eligible_provider",
    domain: "compliance",
    subjectType: "facility",
    assignedRole: "role:facility:manager",
    requiredPermission: "external-provider:manage",
    trigger: { type: "event", on: "appointment.required_forms_attached" },
    completion: { mode: "manual" },
    visibility: "role_pool",
    premium: false,
    baseSafety: false,
    title: "Add an external provider to assign required forms",
  },
];

export const TASK_DEFINITIONS_BY_KEY: Readonly<Record<string, TaskDefinition>> =
  Object.fromEntries(TASK_DEFINITIONS.map((d) => [d.key, d]));

export function getTaskDefinition(key: string): TaskDefinition | undefined {
  return TASK_DEFINITIONS_BY_KEY[key];
}

/**
 * Boot-time registry integrity (KC-10 style): no duplicate `key`s (which would
 * silently last-write-wins in `TASK_DEFINITIONS_BY_KEY`). Cheap insurance as the
 * registry grows; called from the engine plugin's `register()`.
 */
export function assertTaskRegistryValid(): void {
  const seen = new Set<string>();
  for (const def of TASK_DEFINITIONS) {
    if (seen.has(def.key)) {
      throw new Error(`Duplicate TASK_DEFINITIONS key: ${def.key}`);
    }
    seen.add(def.key);
  }
}

/** Base-safety derived resident-care definitions, in registry order. */
export const BASE_SAFETY_DERIVED_DEFINITIONS: readonly TaskDefinition[] =
  TASK_DEFINITIONS.filter(
    (d) =>
      d.baseSafety &&
      d.subjectType === "resident" &&
      d.completion.mode === "derived" &&
      d.derivedKind != null,
  );

/**
 * ALL derived resident-board definitions (base-safety + subscription-gated), in
 * registry order. The board read iterates this filtered by the viewer's resolved
 * `visibleKinds` (which already encodes base-safety + active subscription +
 * capability) — so an opt-in kind appears once a facility subscribes, not just the
 * always-on base-safety four.
 */
export const DERIVED_RESIDENT_BOARD_DEFINITIONS: readonly TaskDefinition[] =
  TASK_DEFINITIONS.filter(
    (d) =>
      d.subjectType === "resident" &&
      d.completion.mode === "derived" &&
      d.derivedKind != null,
  );

// ── Wire shapes (read model / API) ──────────────────────────────────────────────

/** A stateful `task_instances` row as returned over the wire. */
export interface TaskInstanceDTO {
  id: string;
  definitionKey: string;
  /** Resolved definition title; falls back to a stored label if the def retired. */
  title: string;
  domain: TaskDomain | null;
  subjectType: TaskSubjectType;
  subjectId: string;
  facilityId: string;
  assigneeEmployeeId: string | null;
  assignedRole: string;
  visibilityScope: TaskVisibilityScope;
  status: TaskInstanceStatus;
  dueAt: string | null;
  periodKey: string | null;
  createdReason: string;
  sourceType: string;
  sourceId: string;
  completedBy: string | null;
  completedAt: string | null;
  closedReason: string | null;
  createdAt: string;
}

/**
 * A unified task card for the worklist surfaces (My Tasks, facility dashboard).
 * Both a stateful `task_instances` row and a derived (computed-on-read) item map
 * to this shape, so a consumer renders one list. Routing is provenance-driven
 * (`sourceType`/`sourceId` + subject) via `resolveTaskRoute`.
 */
export interface TaskCardDTO {
  id: string;
  /** "stateful" = a task_instances row; "derived" = computed live from a record. */
  source: "stateful" | "derived";
  definitionKey: string;
  title: string;
  detail: string | null;
  domain: TaskDomain | null;
  status: TaskInstanceStatus;
  subjectType: TaskSubjectType;
  subjectId: string;
  sourceType: string;
  sourceId: string;
  dueAt: string | null;
  /** Resolved `priority_override` (lower = higher priority); null = default. */
  priority: number | null;
  /** Action route hint (tab on the resident-detail page, plus an optional
   * tab-specific id, e.g. an appointment id for tab "calendar", plus an
   * optional `view` naming a sub-view WITHIN that tab — set by
   * derived board/oversight items via DerivedTaskItem.route; null for
   * stateful instances (no equivalent field) or items with no specific tab.
   * Consumed by resolveTaskRoute. */
  route: { tab: string; id?: string; view?: string } | null;
}

/** Staff "My Tasks" response. */
export interface MyTasksResponse {
  tasks: TaskCardDTO[];
}

/**
 * One subscribable definition with this scope's current opt-in state — the
 * toggle list for the subscription surface. Base-safety definitions are never
 * listed (always on, not subscribable).
 */
export interface TaskSubscriptionOption {
  definitionKey: string;
  title: string;
  domain: TaskDomain;
  subjectType: TaskSubjectType;
  premium: boolean;
  subscribed: boolean;
  // US-111 per-facility config (null when not subscribed / unconfigured).
  rolePolicy: TaskRolePolicy | null;
  duePolicy: TaskDuePolicy | null;
  priorityOverride: number | null;
  /** System roles that hold the definition's `requiredPermission` — the only
   *  roles assignable as owner / audience (an un-capable role can't complete it). */
  assignableRoles: AppRole[];
  /** Whether a cadence override takes effect for this def (recurring-stateful);
   *  false for derived defs whose cadence is fixed in code (Phase 1). */
  cadenceConfigurable: boolean;
  /** The definition's own recurring frequency (the default in the cadence UI). */
  defaultFrequency: FormFrequency | null;
}

/** Manager-oversight counts for the facility task dashboard. */
export interface FacilityTaskStats {
  open: number;
  overdue: number;
  missed: number;
}

export interface FacilityTaskDashboardResponse {
  stats: FacilityTaskStats;
  /** The actionable (open/overdue) tasks — stateful + derived — newest-due first.
   *  A bounded PREVIEW: `stats` holds the true totals (which can exceed this). */
  attention: TaskCardDTO[];
  /** True when `attention` is a truncated preview of `stats.open + stats.overdue`
   *  — the UI shows "showing N of M" instead of implying the list is complete. */
  attentionTruncated: boolean;
}
